<template>
    <div>
        <header>
            <v-container>
                <v-row no-gutters>
                    <h1 class="font-weight-light headline">
                        {{ $t("title.modules_web") }}
                    </h1>
                    <v-spacer></v-spacer>
                    <v-lazy v-if="isModulesLoaded" transition="fade-transition">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            :label="searchLabel"
                            single-line
                            dense
                            hide-details
                            style="max-width: 200px"
                            class="mr-5 pr-5"
                        ></v-text-field>
                    </v-lazy>
                    <v-lazy transition="fade-transition">
                        <v-btn
                            color="success"
                            dark
                            small
                            @click.stop="
                                dialog.moduleForm = true;
                                isModuleEdited = false;
                                selectedModule = { ...defaultModule };
                            "
                        >
                            {{ $t("general.add") }}
                        </v-btn>
                    </v-lazy>
                </v-row>
            </v-container>
        </header>

        <section class="py-7">
            <v-container>
                <v-sheet class="px-3 pt-3 pb-3" v-if="!isModulesLoaded">
                    <v-skeleton-loader
                        class="mx-auto"
                        type="table"
                    ></v-skeleton-loader>
                </v-sheet>

                <v-card v-else>
                    <v-data-table
                        :headers="headers"
                        :items="modules"
                        :search="search"
                    >
                        <template v-slot:item.action="{ item }">
                            <div style="display: flex">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            v-tr-class-on-hover="'blue'"
                                            size="20px"
                                            color="blue"
                                            class="mr-2"
                                            @click="editModule(item)"
                                            v-on="on"
                                        >
                                            mdi-pencil-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t("general.edit") }}</span>
                                </v-tooltip>

                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            size="22px"
                                            color="red"
                                            v-tr-class-on-hover="'red'"
                                            class="mr-2"
                                            @click="confirmDelete(item)"
                                            v-on="on"
                                        >
                                            mdi-delete-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t("general.delete") }}</span>
                                </v-tooltip>
                            </div>
                        </template>

                        <template v-slot:item.title="{ item }">
                            <div class="text-truncate" style="max-width: 350px">
                                {{ item.title }}
                            </div>
                        </template>

                        <template
                            v-for="language in languages"
                            v-slot:[`item.title_${language.code}`]="{ item }"
                        >
                            <div
                                :key="`module-title-${language.code}-${item.id}`"
                                class="text-truncate"
                                style="max-width: 300px"
                            >
                                {{ item[`title_${language.code}`] || "-" }}
                            </div>
                        </template>

                        <template v-slot:item.is_active="{ item }">
                            <v-switch
                                class="mt-0 pt-0 justify-content-center"
                                hide-details
                                color="green"
                                v-model="item.is_active"
                                @change="changeStatus(item)"
                            ></v-switch>
                        </template>
                    </v-data-table>
                </v-card>

                <ModulesWebForm
                    :module="selectedModule"
                    :languages="languages"
                    :module-names="moduleNames"
                    :visible="dialog.moduleForm"
                    :isEdited="isModuleEdited"
                    @addModule="addModule"
                    @updateModule="updateModule"
                    @close="dialog.moduleForm = false"
                    @snackMessage="snackMessage"
                />

                <ConfirmationDialog
                    :name="selectedModule.title"
                    :text="'modules_web.delete_confirmation'"
                    v-if="dialog.confirmation"
                    @confirm="deleteModule"
                    @cancel="cancelDelete"
                />

                <SnackMessage ref="SnackMessage" />
            </v-container>
        </section>
    </div>
</template>

<script>
import ModulesWebForm from "../components/ModulesWebForm";
import ConfirmationDialog from "../components/ConfirmationDialog";
import SnackMessage from "../components/SnackMessage";

export default {
    data() {
        return {
            languages: [],
            modules: [],
            moduleNames: [],
            isModulesLoaded: false,
            search: "",
            dialog: {
                confirmation: false,
                moduleForm: false,
            },
            selectedModule: {
                id: null,
                module_name_id: null,
                title: "",
                subtitle: "",
                description: "",
                tag_text: "",
                btn_link: "",
                btn_text: "",
                icon_tag: "",
                icon_image: "",
                is_active: 0,
            },
            defaultModule: {
                id: null,
                module_name_id: null,
                title: "",
                subtitle: "",
                description: "",
                tag_text: "",
                btn_link: "",
                btn_text: "",
                icon_tag: "",
                icon_image: "",
                is_active: 0,
            },
            isModuleEdited: false,
        };
    },

    components: {
        ModulesWebForm,
        ConfirmationDialog,
        SnackMessage,
    },

    computed: {
        headers() {
            const baseHeaders = [
                {
                    text: this.$t("general.actions"),
                    value: "action",
                    sortable: false,
                    width: "100px",
                },
                {
                    text: this.$t("modules_web.title_cz"),
                    align: "left",
                    sortable: true,
                    value: "title",
                },
            ];

            const languageHeaders = this.languages.map((lang) => ({
                text:
                    this.$t("modules_web.title_lang") +
                    " " +
                    lang.name +
                    " (" +
                    lang.code +
                    ")",
                align: "left",
                sortable: false,
                value: `title_${lang.code}`,
            }));

            const activeHeader = {
                text: this.$t("general.active"),
                align: "center",
                sortable: false,
                value: "is_active",
                width: "100px",
            };

            return [...baseHeaders, ...languageHeaders, activeHeader];
        },
        searchLabel() {
            return this.$t("general.search");
        },
    },

    created() {
        this.getModules();
    },

    watch: {
        languages(newLanguages) {
            const base = {
                id: null,
                module_name_id: null,
                title: "",
                subtitle: "",
                description: "",
                tag_text: "",
                btn_link: "",
                btn_text: "",
                icon_tag: "",
                icon_image: "",
                is_active: 0,
            };

            newLanguages.forEach((lang) => {
                base[`title_${lang.code}`] = "";
                base[`subtitle_${lang.code}`] = "";
                base[`description_${lang.code}`] = "";
                base[`tag_text_${lang.code}`] = "";
                base[`btn_text_${lang.code}`] = "";
            });

            this.defaultModule = base;

            if (!this.selectedModule.id) {
                this.selectedModule = { ...base };
            }
        },
    },

    methods: {
        snackMessage(msg, type) {
            this.$refs.SnackMessage.showMessage(msg, type);
        },

        async getModules() {
            try {
                const response = await axios.get("/modules-web", {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                });

                const { languages, modules, module_names } = response.data;

                this.languages = (languages || []).filter(
                    (lang) => lang.code !== "cz"
                );

                this.modules = modules || [];
                this.moduleNames = module_names || [];
                this.isModulesLoaded = true;
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },

        addModule(module) {
            this.modules.push(module);
        },

        editModule(item) {
            this.selectedModule = { ...item };
            this.dialog.moduleForm = true;
            this.isModuleEdited = true;
        },

        updateModule(module) {
            const index = this.modules.findIndex(
                (item) => item.id == module.id
            );
            if (index !== -1) {
                this.modules.splice(index, 1, module);
            }
        },

        confirmDelete(item) {
            this.selectedModule = item;
            this.dialog.confirmation = true;
        },

        cancelDelete() {
            this.dialog.confirmation = false;
        },

        async deleteModule() {
            this.dialog.confirmation = false;
            const index = this.modules.indexOf(this.selectedModule);

            try {
                await axios.delete(`/modules-web/${this.selectedModule.id}`, {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                });

                if (index !== -1) {
                    this.modules.splice(index, 1);
                }
                this.selectedModule = { ...this.defaultModule };
                this.snackMessage(
                    "modules_web.deleted_successfully",
                    "success"
                );
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },

        async changeStatus(item) {
            try {
                await axios.put(
                    `/modules-web/status/${item.id}`,
                    { is_active: item.is_active },
                    {
                        headers: {
                            Authorization:
                                "Bearer " +
                                this.$store.state.tokenData.user.access_token,
                        },
                    }
                );
                this.snackMessage("general.item_saved", "success");
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },
    },
};
</script>
